import { ImageTemplate, Prisma } from "@prisma/client";
import type { RoleType, SocialPlatform } from "@prisma/client";

export const PLATFORMS: SocialPlatform[] = [
  "instagram",
  "facebook",
  "linkedin",
  "twitter",
];

export const USER_ROLES: RoleType[] = [
  "readonly",
  "editor",
  "admin",
  "account_manager",
  "owner",
];

export const SUPPORTED_LANGUAGES = [
  { code: "en-US", name: "English (US)" },
  { code: "en-GB", name: "English (UK)" },
  { code: "es-ES", name: "Spanish (Spain)" },
  { code: "fr-FR", name: "French (France)" },
  { code: "de-DE", name: "German (Germany)" },
  { code: "pt-BR", name: "Portuguese (Brazil)" },
  { code: "zh-CN", name: "Chinese (Simplified)" },
  { code: "ja-JP", name: "Japanese" },
  { code: "ru-RU", name: "Russian" },
  { code: "ar-SA", name: "Arabic (Saudi Arabia)" },
  { code: "hi-IN", name: "Hindi" },
  { code: "ar-AE", name: "Arabic (United Arab Emirates)" },
  { code: "ar-BH", name: "Arabic (Bahrain)" },
  { code: "ar-EG", name: "Arabic (Egypt)" },
  { code: "ar-JO", name: "Arabic (Jordan)" },
  { code: "ar-KW", name: "Arabic (Kuwait)" },
  { code: "ar-LB", name: "Arabic (Lebanon)" },
  { code: "ar-OM", name: "Arabic (Oman)" },
  { code: "ar-QA", name: "Arabic (Qatar)" },
  { code: "bn-IN", name: "Bengali" },
  { code: "cs-CZ", name: "Czech" },
  { code: "da-DK", name: "Danish" },
  { code: "el-GR", name: "Greek" },
  { code: "es-AR", name: "Spanish (Argentina)" },
  { code: "es-CL", name: "Spanish (Chile)" },
  { code: "es-CO", name: "Spanish (Colombia)" },
  { code: "es-MX", name: "Spanish (Mexico)" },
  { code: "es-PE", name: "Spanish (Peru)" },
  { code: "es-PR", name: "Spanish (Puerto Rico)" },
  { code: "es-VE", name: "Spanish (Venezuela)" },
  { code: "fi-FI", name: "Finnish" },
  { code: "fr-CA", name: "French (Canada)" },
  { code: "he-IL", name: "Hebrew" },
  { code: "hu-HU", name: "Hungarian" },
  { code: "id-ID", name: "Indonesian" },
  { code: "it-IT", name: "Italian" },
  { code: "ko-KR", name: "Korean" },
  { code: "ms-MY", name: "Malay" },
  { code: "nl-NL", name: "Dutch" },
  { code: "no-NO", name: "Norwegian" },
  { code: "pl-PL", name: "Polish" },
  { code: "pt-PT", name: "Portuguese (Portugal)" },
  { code: "ro-RO", name: "Romanian" },
  { code: "sv-SE", name: "Swedish" },
  { code: "ta-IN", name: "Tamil" },
  { code: "th-TH", name: "Thai" },
  { code: "tr-TR", name: "Turkish" },
  { code: "uk-UA", name: "Ukrainian" },
  { code: "vi-VN", name: "Vietnamese" },
  { code: "zh-TW", name: "Chinese (Traditional)" },
] as const;

export const FONT_URLS = {
  VT323: "https://fonts.gstatic.com/s/vt323/v17/pxiKyp0ihIEF2isfFJXUdVNF.woff2",
  Pacifico:
    "https://fonts.gstatic.com/s/pacifico/v22/FwZY7-Qmy14u9lezJ-6H6MmBp0u-.woff2",
  Lato100:
    "https://fonts.gstatic.com/s/lato/v24/S6u8w4BMUTPHh30AXC-qNiXg7Q.woff2",
  Lato900:
    "https://fonts.gstatic.com/s/lato/v24/S6u9w4BMUTPHh50XSwiPGQ3q5d0.woff2",
  Montserrat:
    "https://fonts.gstatic.com/s/montserrat/v30/JTUSjIg1_i6t8kCHKm459WlhyyTh89Y.woff2",
};

export const NARRATIVE_STYLES = [
  {
    title: "Personal",
    description: "Post as yourself",
    instruction:
      "Use a personal tone, e.g. 'I'm doing this...' or 'I'm excited to share...'.",
    example:
      "I'm excited to announce our new sustainable packaging initiative! 🌱 I can't wait to show you how we're making a difference in reducing plastic waste.",
  },
  {
    title: "Company",
    description: "Post on behalf of the company",
    instruction:
      "Use a company voice, e.g. 'We at [Company Name] are...' or 'Our team is proud to...'",
    example:
      "We at [Company Name] are proud to introduce our new sustainable packaging initiative! 🌱 Our team has been working hard to reduce plastic waste.",
  },
];

export const POST_LENGTH = [
  {
    length: "Short",
    title: "Concise",
    description: "Up to 20 words",
    example:
      "Check out our new summer collection! Perfect for your beach vacation. #Fashion #Summer",
  },
  {
    length: "Medium",
    title: "Standard",
    description: "20-100 words",
    example:
      "Introducing our new summer collection! We've combined comfort with style in every piece. Our sustainable materials and expert craftsmanship ensure you'll look and feel amazing. Perfect for those sunny days ahead. Join us in making fashion more sustainable! 🌿 #SustainableFashion",
  },
  {
    length: "Long",
    title: "Extended",
    description: "100+ words",
    example:
      "🎉 We're thrilled to announce the launch of our summer collection! This season, we've outdone ourselves with a stunning array of sustainable fashion pieces. Each item is thoughtfully designed with both style and environmental impact in mind. Our team spent months sourcing the finest eco-friendly materials and working with ethical manufacturers to bring you a collection that makes a difference. From breezy sundresses to versatile separates, every piece tells a story of craftsmanship and dedication. We believe fashion can be both beautiful and responsible. Join us in our mission to transform the industry, one garment at a time. Swipe through to discover your new summer favorites! 🌿 #SustainableFashion #EthicalFashion #SummerCollection",
  },
];

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const AI_MODELS = {
  image: {
    stability: {
      vendor: "replicate",
      name: "stability-ai/sdxl",
      model:
        "stability-ai/sdxl:39ed52f2a78e934b3ba6e2a89f5b1c712de7dfea535525255b1aa35c5565e08b",
      maxTokens: 16384,
      temperature: 0.5,
      topP: 1,
    },
    flux: {
      vendor: "replicate",
      name: "black-forest-labs/flux-1.1-pro",
      model: "black-forest-labs/flux-1.1-pro",
      maxTokens: 16384,
      temperature: 0.5,
    },
    flux_ultra: {
      vendor: "replicate",
      name: "black-forest-labs/flux-1.1-pro-ultra",
      model: "black-forest-labs/flux-1.1-pro-ultra",
      maxTokens: 16384,
      temperature: 0.5,
    },
  },
  llm: {
    "gpt-4o": {
      vendor: "openai",
      name: "gpt-4o",
      model: "gpt-4o",
      maxTokens: 16384,
      temperature: 0.5,
      topP: 1,
    },
    "gpt-4o-mini": {
      vendor: "openai",
      name: "gpt-4o-mini",
      model: "gpt-4o-mini",
      maxTokens: 16384,
      temperature: 0.5,
    },
    "claude-3.5-sonnet": {
      vendor: "anthropic",
      name: "claude-3.5-sonnet",
      model: "us.anthropic.claude-3-5-sonnet-20241022-v2:0",
      maxTokens: 16384,
      temperature: 0.5,
      topP: 1,
    },
    "claude-3.7-sonnet": {
      vendor: "anthropic",
      name: "claude-3.7-sonnet",
      model: "us.anthropic.claude-3-7-sonnet-20250219-v1:0",
      maxTokens: 16384,
      temperature: 0.5,
      topP: 1,
    },
    "grok-beta": {
      vendor: "xai",
      name: "grok-beta",
      model: "grok-beta",
      maxTokens: 16384,
      temperature: 0.5,
      topP: 1,
    },
  },
};

export const GENERIC_EMAIL_DOMAINS = [
  "gmail",
  "yahoo",
  "hotmail",
  "outlook",
  "aol",
  "icloud",
  "protonmail",
  "zoho",
  "mail",
  "gmx",
  "yandex",
  "inbox",
  "fastmail",
  "tutanota",
  "hushmail",
  "runbox",
  "mailfence",
  "torguard",
  "ctemplar",
  "disroot",
  "kolabnow",
  "mailbox",
  "posteo",
  "startmail",
];

export const SECOND_LEVEL_DOMAINS = {
  au: "com.au",
  br: "com.br",
  jp: "co.jp",
  za: "co.za",
  mx: "com.mx",
  in: "co.in",
  nz: "co.nz",
  cn: "com.cn",
  kr: "co.kr",
  sg: "com.sg",
  tw: "com.tw",
  hk: "com.hk",
  tr: "com.tr",
  il: "co.il",
  my: "com.my",
  ph: "com.ph",
  th: "co.th",
  ar: "com.ar",
  ve: "com.ve",
  id: "co.id",
};
export const WEEKDAYS: string[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const SOCIAL_LINK_PATTERNS = {
  instagram: /(?:http:\/\/|https:\/\/|)(?:www\.|)instagram\.com\/([^\/?#&]+).*/,
  facebook: /(?:http:\/\/|https:\/\/|)(?:www\.|)facebook\.com\/([^\/?#&]+).*/,
  linkedin:
    /(?:http:\/\/|https:\/\/|)(?:www\.|)linkedin\.com\/in|company\/([^\/?#&]+).*/,
  twitter: /(?:http:\/\/|https:\/\/|)(?:www\.|)twitter\.com\/([^\/?#&]+).*/,
  github: /(?:http:\/\/|https:\/\/|)(?:www\.|)github\.com\/([^\/?#&]+).*/,
};

export const defaultTextPrompt = `Take a step back and consider the information I have given you, Considering the description of the business. Consider the sample of posts and how I have spoken in them.
  with all this considered and using the article in my previous message as inspiration, carefully craft a social media post for my business and fits in with the tone of voice.
  The post must be no more than 2000 characters long. Be detailed and very specific, mentioning all important facts and figures.
  This is content marketing, do not mention the brand unless it is a post specifically about their products and services.`;

export const DEFAULT_BRAND_SETTINGS: Prisma.BrandSettingsCreateInput = {
  description: "My Brand",
  systemPrompt:
    "You are a world class digital marketing consultant. Users will give you information about their company and you will create high quality social media posts for them. You will be paid $2000 for each post you create",
  defaultPrompt: `${defaultTextPrompt}

  Include a detailed description of an image goes with the post the
  Include explicit details of the lens type, lighting, image focus.
  be purely descriptive. do not talk about what the image symbolises. The image should not contain any Text
  75 words max.`,
  colors: [],
  imageTemplates: [],
};

export const POST_LIMITATIONS: Record<
  "twitter" | "facebook" | "instagram" | "linkedin",
  { text: number; image: { width: number; height: number } }
> = {
  twitter: {
    text: 280,
    image: { width: 1440, height: 1440 },
  },
  instagram: {
    text: 2200,
    image: { width: 1440, height: 1440 },
  },
  facebook: {
    text: 63206,
    image: { width: 1440, height: 1440 },
  },
  linkedin: {
    text: 3000,
    image: { width: 1440, height: 1440 },
  },
};

export const socialButtonColors = {
  instagram: "bg-gradient-to-br from-purple-600 via-red-600 to-yellow-600",
  facebook: "bg-facebook",
  linkedin: "bg-blue-600",
  twitter: "bg-twitter",
};

type TaskDef = {
  id: string;
  position: { x: number; y: number };
  name: string;
  parentType: string;
  subtype: string;
  config: any;
  input: string[];
};

function defaultWorkflow(
  sourceName: string,
  sourceType:
    | "list"
    | "url_list"
    | "rss"
    | "website"
    | "news"
    | "shopify_products"
    | "list"
) {
  return ({
    platforms,
    image_template,
  }: {
    platforms: Array<(typeof PLATFORMS)[number]>;
    user: { email: string };
    image_template: ImageTemplate | null | undefined;
  }) => {
    const hasTwitter = platforms.includes("twitter") || platforms.includes("x");
    const hasOthers = platforms.some((p) => p !== "twitter" && p !== "x");

    const tasks: TaskDef[] = [
      {
        id: "trigger",
        position: { x: 50, y: 100 },
        parentType: "trigger",
        subtype: "schedule",
        config: {
          schedule: {
            day: "thursday",
            time: {
              hour: Math.floor(Math.random() * 24),
              minute: Math.floor(Math.random() * 60),
            },
            repeat: "weekly",
          },
        },
        name: "start",
        input: [],
      },
      {
        id: "contentsource",
        position: { x: 300, y: 100 },
        parentType: "contentsource",
        subtype: sourceType,
        name: sourceName,
        config: {},
        input: ["trigger"],
      },
    ];

    let createImageTask: TaskDef;
    if (image_template && image_template.type === "reference_image") {
      createImageTask = {
        id: "image",
        position: { x: 600, y: 1 },
        parentType: "media",
        subtype: "look-a-like",
        config: {
          image_template,
        },
        name: "Create Image",
        input: [],
      };
    } else {
      createImageTask = {
        id: "image",
        position: { x: 600, y: 1 },
        parentType: "media",
        subtype: "ai_image",
        config: {
          image_template,
        },
        name: "Create Image",
        input: [],
      };
    }

    if (hasTwitter) {
      tasks.push({
        id: "twitter_caption",
        position: { x: 600, y: 200 },
        parentType: "text",
        subtype: "twitter_caption",
        name: "X/Twitter Caption",
        config: {},
        input: ["contentsource"],
      });

      if (!hasOthers) {
        createImageTask.input.push("contentsource");
      }
      tasks.push({
        id: "twitter_post",
        position: { x: 900, y: 200 },
        parentType: "post",
        subtype: "post",
        name: "X/Twitter Post",
        config: { platforms: ["twitter"] },
        input: ["twitter_caption", "image"],
      });
    }

    const withoutTwitter = platforms.filter(
      (p) => p !== "twitter" && p !== "x"
    );

    if (!hasTwitter || hasOthers) {
      tasks.push({
        id: "post_caption",
        parentType: "text",
        position: { x: 600, y: 100 },
        subtype: "regular_caption",
        name: "Post Caption",
        config: {},
        input: ["contentsource"],
      });

      createImageTask.input.push("post_caption");

      tasks.push({
        id: "post",
        position: { x: 900, y: 100 },
        parentType: "post",
        subtype: "post",
        name: "Post",
        config: { platforms: withoutTwitter },
        input: ["post_caption", "image"],
      });
    }

    tasks.push(createImageTask);

    return tasks;
  };
}

type PostType = {
  title: string;
  description: string;
  suitable_for: string;
  image_template?: string | string[];
  ai_instructions: string;
  tasks: ReturnType<typeof defaultWorkflow>;
};

export const POST_TYPES: Record<string, PostType> = {
  meet_the_team: {
    title: "Meet the team",
    description: "Meet the team",
    suitable_for: "businesses with larger teams",
    image_template: "title_left",
    ai_instructions:
      "Choose the most appropriate employees collection from the collections library if no employees collection exists leave the field empty for user to add employees manually",
    tasks: defaultWorkflow("Employees", "list"),
  },
  news: {
    title: "News",
    description:
      "A post detailing some news or a trend that's relevant for the audience",
    suitable_for: "B2B",
    image_template: ["reference_image", "title_bottom"],
    ai_instructions:
      "configure the news source to find news relevant to the brands",
    tasks: defaultWorkflow("News", "news"),
  },
  testimonials: {
    title: "Testimonials",
    image_template: ["reference_image", "testamonial"],
    description: "A post with a quote from a satisfied customer",
    ai_instructions:
      "Should default to an empty list, only use the website if it contains a testimonials section",
    suitable_for: "Businesses with a testimonials section on their website",
    tasks: defaultWorkflow("Testimonials", "list"),
  },
  case_studies: {
    title: "Case studies",
    image_template: ["reference_image", "title_bottom"],
    description:
      "A post showing an example of a successful result for a customer and detailing how that was achieved",
    suitable_for: "Medical, B2B, service businesses, before/after",
    tasks: defaultWorkflow("Case Studies", "url_list"),
  },
  product_benefits: {
    title: "Product benefits",
    image_template: ["reference_image", "title_left"],
    description: "A post detailing the benefits of the service for a customera",
    suitable_for: "B2B, service businesses, product businesses",
    ai_instructions:
      "for the contentsource: generate a list of product benefits based on the website content",
    tasks: defaultWorkflow("Product Benefits", "list"),
  },
  product_post: {
    title: "Product post",
    description: "Directly promotional post",
    suitable_for: "Product businesses",
    ai_instructions:
      "Choose the most appropriate products collection from the collections library",
    tasks: defaultWorkflow("Products", "url_list"),
  },
  shopify_product_post: {
    title: "Product post from Shopify",
    description: "Directly promotional post from Shopify products",
    suitable_for: "Product businesses with Shopify",
    ai_instructions:
      "Choose the most appropriate shopify products collection from the collections library",
    tasks: ({
      platforms,
      user,
    }: {
      platforms: Array<(typeof PLATFORMS)[number]>;
      user: { email: string };
    }) => {
      const tasks: TaskDef[] = [
        {
          id: "trigger",
          name: "start",
          position: { x: 50, y: 100 },
          parentType: "trigger",
          subtype: "schedule",
          config: {
            schedule: {
              day: "thursday",
              time: {
                hour: Math.floor(Math.random() * 24),
                minute: Math.floor(Math.random() * 60),
              },
              repeat: "weekly",
            },
          },
          input: [],
        },
        {
          id: "contentsource",
          position: { x: 300, y: 100 },
          parentType: "contentsource",
          subtype: "shopify_products",
          name: "Shopify Products",
          config: { outputs: ["text", "images"], rotate: true },
          input: ["trigger"],
        },
      ];

      let twitter = false;

      if (platforms.includes("twitter") || platforms.includes("x")) {
        twitter = true;
        tasks.push({
          id: "twitter_caption",
          position: { x: 600, y: 200 },
          parentType: "text",
          subtype: "twitter_caption",
          name: "X/Twitter Caption",
          config: { outputs: ["text", "images"] },
          input: ["contentsource"],
        });
        tasks.push({
          id: "twitter_post",
          position: { x: 900, y: 200 },
          parentType: "post",
          subtype: "post",
          name: "X/Twitter Post",
          config: { platforms: ["twitter"] },
          input: ["twitter_caption"],
        });
      }

      const withoutTwitter = platforms.filter(
        (p) => p !== "twitter" && p !== "x"
      );

      if (!twitter || withoutTwitter.length > 0) {
        tasks.push({
          id: "post_caption",
          name: "Post Caption",
          parentType: "text",
          position: { x: 600, y: 100 },
          subtype: "regular_caption",
          config: { outputs: ["text", "images"] },
          input: ["contentsource"],
        });

        tasks.push({
          id: "post",
          position: { x: 900, y: 100 },
          parentType: "post",
          subtype: "post",
          name: "Post",
          config: { platforms: withoutTwitter },
          input: ["post_caption"],
        });
      }

      return tasks;
    },
  },
  engagement_question: {
    title: "Engagement question",
    image_template: ["reference_image", "title_center"],
    description:
      "An onbrand question that triggers engagement from the audience",
    suitable_for: "All businesses",
    ai_instructions:
      "for the contentsource: generate a list of questions that are relevant to the business",
    tasks: defaultWorkflow("Engagement Questions", "list"),
  },
  tips_challenges: {
    title: "Tips / Challenges for customer profiles",
    image_template: ["reference_image", "title_center"],
    description:
      "A post detailing a common challenge that customers face related to the business product, or tips for same",
    suitable_for: "All businesses",
    ai_instructions:
      "for the contentsource: generate a list of challenges or tips that are relevant to the business",
    tasks: defaultWorkflow("Tips", "list"),
  },
  trivia: {
    title: "Trivia",
    image_template: ["reference_image", "title_center"],
    description:
      "A post with relevant trivia for the brand that triggers engagement from the audience",
    suitable_for: "All businesses",
    ai_instructions:
      "for the contentsource: generate a list of trivia questions that are relevant to the business",
    tasks: defaultWorkflow("Trivia Questions", "list"),
  },
  challenge_posts: {
    title: "Challenge posts",
    image_template: ["reference_image", "title_center"],
    description:
      "A weekly challenge related to your product that your audience can get involved in",
    suitable_for: "All businesses",
    ai_instructions:
      "for the contentsource: generate a list of challenges that are relevant to the business",
    tasks: defaultWorkflow("Challanges", "list"),
  },
  industry_insights: {
    title: "Industry Insights",
    image_template: ["reference_image", "title_left"],
    description:
      "A post with relevant industry insights for the brand that triggers engagement from the audience",
    suitable_for: "B2B",
    contentsource: ["list"],
    tasks: defaultWorkflow("Insights", "list"),
  },
  selfie_video: {
    title: "Selfie video",
    suitable_for: "All businesses",
    ai_instructions:
      "for the contentsource: generate a list 10 interesting selfie video ideas, that can be talked about by the business",
    tasks: ({
      platforms,
      user,
    }: {
      platforms: Array<(typeof PLATFORMS)[number]>;
      user: { email: string };
    }) => {
      const tasks: TaskDef[] = [
        {
          id: "trigger",
          name: "start",
          position: { x: 50, y: 100 },
          parentType: "trigger",
          subtype: "schedule",
          config: {
            schedule: {
              day: "thursday",
              time: {
                hour: Math.floor(Math.random() * 24),
                minute: Math.floor(Math.random() * 60),
              },
              repeat: "weekly",
            },
          },
          input: [],
        },
        {
          id: "contentsource",
          position: { x: 300, y: 100 },
          parentType: "contentsource",
          subtype: "list",
          name: "Video Ideas",
          config: {},
          input: ["trigger"],
        },
        {
          id: "video_script",
          parentType: "text",
          position: { x: 600, y: 0 },
          subtype: "video_script",
          config: {},
          name: "Video Script",
          input: ["contentsource"],
        },
        {
          id: "send_email",
          parentType: "action",
          position: { x: 900, y: 0 },
          config: {
            email: user?.email!,
          },
          subtype: "email",
          name: "Send Email",
          input: ["video_script"],
        },
      ];

      let twitter = false;

      if (platforms.includes("twitter") || platforms.includes("x")) {
        twitter = true;
        tasks.push({
          id: "twitter_caption",
          position: { x: 600, y: 200 },
          parentType: "text",
          subtype: "twitter_caption",
          name: "X/Twitter Caption",
          config: {},
          input: ["contentsource"],
        });
        tasks.push({
          id: "twitter_post",
          position: { x: 900, y: 200 },
          parentType: "post",
          subtype: "post",
          name: "X/Twitter Post",
          config: { platforms: ["twitter"] },
          input: ["twitter_caption"],
        });
      }

      const withoutTwitter = platforms.filter(
        (p) => p !== "twitter" && p !== "x"
      );

      if (!twitter || withoutTwitter.length > 0) {
        tasks.push({
          id: "post_caption",
          name: "Post Caption",
          parentType: "text",
          position: { x: 600, y: 100 },
          subtype: "regular_caption",
          config: {},
          input: ["contentsource"],
        });

        tasks.push({
          id: "post",
          position: { x: 900, y: 100 },
          parentType: "post",
          subtype: "reel",
          name: "Reel",
          config: { platforms: withoutTwitter },
          input: ["post_caption"],
        });
      }

      return tasks;
    },
  },
  custom: {
    title: "Custom Post Type",
    suitable_for: "B2B",
    tasks: defaultWorkflow("Content", "list"),
  },
};

export const getPostTypes = ({
  platforms,
  user,
  imageTemplates,
}: {
  platforms: Array<(typeof PLATFORMS)[number]>;
  user: { email: string };
  // brand: Brand & { setting: BrandSettings };
  imageTemplates: ImageTemplate[];
}) => {
  return Object.fromEntries(
    Object.entries(POST_TYPES).map(([key, value]) => {
      if (typeof value.tasks == "function") {
        let image_template: ImageTemplate | undefined = undefined;
        if (imageTemplates?.length && value.image_template) {
          if (typeof value.image_template == "string") {
            image_template = imageTemplates.find((t) => t.name === value.image_template)
          } else if (Array.isArray(value.image_template)) {
            value.image_template.forEach((template: string) => {
              if (image_template) return
              if (template == 'reference_image'){
                console.log('reference_image')
                image_template = imageTemplates.find((t) => t.type === "reference_image")

                console.log('image_template', image_template)
              } else {
                console.log('template', template)
                image_template = imageTemplates.find((t) => t.name === template)
              }
            })
          }
        }

        return [key, { ...value, tasks: value.tasks({ platforms, user, image_template }) }];
      }
      return [key, value];
    })
  );
};
